// src/pages/BecomeMerchantPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, addDoc, query, where, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import {
  Store, Send, CheckCircle, Clock, XCircle, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = [
  'Handmade Gifts', 'Personalized Gifts', 'Art & Paintings',
  'Jewellery', 'Home Decor', 'Clothing & Accessories',
  'Food & Sweets', 'Books & Stationery', 'Toys & Games', 'Other',
];

export default function BecomeMerchantPage() {
  const { user } = useApp();
  const [existingRequest, setExistingRequest] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState({
    shopName:    '',
    category:    '',
    description: '',
    experience:  '',
    socialLink:  '',
    phone:       '',
  });

  // Check if user already submitted a request
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const checkRequest = async () => {
      try {
        const q    = query(
          collection(db, 'merchantRequests'),
          where('userId', '==', user.uid)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const req = { id: snap.docs[0].id, ...snap.docs[0].data() };
          setExistingRequest(req);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    checkRequest();
  }, [user]);

  const change = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.shopName.trim())    { toast.error('Shop name is required'); return; }
    if (!form.category)           { toast.error('Please select a category'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.phone.trim())       { toast.error('Phone number is required'); return; }

    setSaving(true);
    try {
      const docRef = await addDoc(collection(db, 'merchantRequests'), {
        userId:      user.uid,
        userEmail:   user.email,
        userName:    user.displayName,
        shopName:    form.shopName.trim(),
        category:    form.category,
        description: form.description.trim(),
        experience:  form.experience.trim(),
        socialLink:  form.socialLink.trim(),
        phone:       form.phone.trim(),
        status:      'pending',
        submittedAt: serverTimestamp(),
      });
      setExistingRequest({
        id: docRef.id, status: 'pending', shopName: form.shopName, submittedAt: new Date(),
      });
      toast.success('Application submitted! ✅ Admin will review it shortly.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return (
    <div className="min-h-screen bg-cream flex items-center justify-center pt-20 p-4">
      <div className="text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="font-display font-bold text-2xl text-gray-800 mb-4">Please login first</h2>
        <Link to="/login" className="bg-rose-500 text-white px-6 py-3 rounded-2xl hover:bg-rose-600 transition-colors">
          Login / Sign Up
        </Link>
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
    </div>
  );

  // Already a merchant
  if (user.role === 'merchant' || user.role === 'admin') return (
    <div className="min-h-screen bg-cream flex items-center justify-center pt-20 p-4">
      <div className="bg-white rounded-3xl shadow-card p-10 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-500" />
        </div>
        <h2 className="font-display font-bold text-2xl text-gray-900 mb-2">You're already a merchant! 🎉</h2>
        <p className="text-gray-400 font-body mb-6">You have full access to the merchant dashboard.</p>
        <Link to="/merchant" className="bg-rose-500 text-white px-8 py-3.5 rounded-2xl font-medium hover:bg-rose-600 transition-colors inline-block">
          Go to Dashboard →
        </Link>
      </div>
    </div>
  );

  // Request already submitted
  if (existingRequest) {
    const statusConfig = {
      pending:  { icon: Clock,        color: 'text-amber-500',  bg: 'bg-amber-50',  border: 'border-amber-200', label: 'Under Review',  message: 'Your application is being reviewed by our admin team. We will get back to you within 1–2 business days.' },
      approved: { icon: CheckCircle,  color: 'text-green-500',  bg: 'bg-green-50',  border: 'border-green-200', label: 'Approved! 🎉',  message: 'Congratulations! Your merchant request has been approved. Please refresh the page to access your merchant dashboard.' },
      rejected: { icon: XCircle,      color: 'text-red-500',    bg: 'bg-red-50',    border: 'border-red-200',   label: 'Not Approved',  message: 'Your request was not approved this time. You may contact us for more information or resubmit with more details.' },
    };
    const cfg = statusConfig[existingRequest.status] || statusConfig.pending;
    const StatusIcon = cfg.icon;

    return (
      <div className="min-h-screen bg-cream flex items-center justify-center pt-20 p-4">
        <div className="bg-white rounded-3xl shadow-card p-10 max-w-md w-full text-center">
          <div className={`w-16 h-16 ${cfg.bg} border ${cfg.border} rounded-full flex items-center justify-center mx-auto mb-5`}>
            <StatusIcon size={32} className={cfg.color} />
          </div>
          <h2 className="font-display font-bold text-2xl text-gray-900 mb-2">Application {cfg.label}</h2>
          <p className="text-gray-400 font-body text-sm mb-4">{cfg.message}</p>
          <div className={`${cfg.bg} border ${cfg.border} rounded-2xl p-4 mb-6 text-left`}>
            <p className="text-xs text-gray-400 font-body">Shop Name</p>
            <p className="font-body font-semibold text-gray-800">{existingRequest.shopName}</p>
            <p className="text-xs text-gray-400 font-body mt-2">Status</p>
            <p className={`font-body font-bold capitalize ${cfg.color}`}>{existingRequest.status}</p>
          </div>
          {existingRequest.status === 'rejected' && (
            <button onClick={() => setExistingRequest(null)}
              className="bg-rose-500 text-white px-6 py-3 rounded-2xl font-medium hover:bg-rose-600 transition-colors">
              Resubmit Application
            </button>
          )}
          {existingRequest.status === 'approved' && (
            <button onClick={() => window.location.reload()}
              className="bg-rose-500 text-white px-6 py-3 rounded-2xl font-medium hover:bg-rose-600 transition-colors">
              Refresh & Go to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-gray-800 text-sm bg-gray-50 focus:bg-white transition-all";

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="text-center mb-10 pt-8">
          <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Store size={32} className="text-rose-500" />
          </div>
          <h1 className="font-display font-bold text-3xl text-gray-900 mb-3">Become a Merchant</h1>
          <p className="text-gray-500 font-body text-base max-w-md mx-auto">
            Sell your handmade and personalized gifts on CoupleCart. Fill in the form below and our team will review your application.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { emoji: '🛍️', title: 'Sell to 10K+ Couples', desc: 'Access our growing customer base' },
            { emoji: '💰', title: 'Keep 90% Revenue',     desc: 'Only 10% platform commission'     },
            { emoji: '📦', title: 'Easy Shipping',        desc: 'Integrated Shiprocket pickup'     },
          ].map(({ emoji, title, desc }) => (
            <div key={title} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
              <div className="text-2xl mb-2">{emoji}</div>
              <p className="font-body font-semibold text-gray-900 text-sm">{title}</p>
              <p className="font-body text-gray-400 text-xs mt-0.5">{desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
          <h2 className="font-display font-bold text-lg text-gray-900 mb-2">Application Details</h2>

          <div>
            <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
              Shop / Brand Name <span className="text-rose-500">*</span>
            </label>
            <input type="text" value={form.shopName} onChange={change('shopName')}
              placeholder="e.g. Sneha's Handmade Studio" className={inputClass} />
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
              Product Category <span className="text-rose-500">*</span>
            </label>
            <select value={form.category} onChange={change('category')} className={inputClass}>
              <option value="">Select a category</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
              What do you sell? <span className="text-rose-500">*</span>
            </label>
            <textarea value={form.description} onChange={change('description')} rows={4}
              placeholder="Describe your products, materials used, what makes them special, how long you've been making them..."
              className={`${inputClass} resize-none`} />
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
              Previous Experience / Where do you currently sell?
            </label>
            <textarea value={form.experience} onChange={change('experience')} rows={2}
              placeholder="Instagram shop, local markets, Etsy, etc. (optional)"
              className={`${inputClass} resize-none`} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
                Phone Number <span className="text-rose-500">*</span>
              </label>
              <input type="tel" value={form.phone} onChange={change('phone')}
                placeholder="10-digit mobile" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
                Instagram / Portfolio Link
              </label>
              <input type="url" value={form.socialLink} onChange={change('socialLink')}
                placeholder="https://instagram.com/yourshop" className={inputClass} />
            </div>
          </div>

          {/* Terms */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-xs text-rose-700 font-body leading-relaxed">
            By submitting this form, you agree to CoupleCart's seller terms. CoupleCart takes a 10% commission on each sale. Payouts are processed within 3 days of order delivery.
          </div>

          <button type="submit" disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-2xl font-body font-semibold text-base transition-all shadow-[0_4px_20px_rgba(244,63,94,0.3)] disabled:opacity-60">
            {saving
              ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              : <Send size={18} />
            }
            {saving ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}
